import "../styles/DashBoard.css";
import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import Studds_Logo from "../assets/studds_logo.png";
import { getCurrentUserRole, getSidebarMenuItems, normalizeRole, hasPermission } from "../utils/rbac";
import { apiFetch } from "../services/api";
import { MdVerifiedUser } from "react-icons/md";

function DashBoard() {
  const [role, setRole] = useState(getCurrentUserRole());
  const [isNavHide, setNavHide] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Dynamic sidebar menu items based on active role
  const menuItems = getSidebarMenuItems(role);

  // Listen for roleChanged events (from EditUserModal, apiFetch, or storage)
  useEffect(() => {
    const handleRoleChange = (e: any) => {
      const newRole = e?.detail?.role ? normalizeRole(e.detail.role) : getCurrentUserRole();
      setRole(newRole);
    };

    window.addEventListener("roleChanged", handleRoleChange);
    window.addEventListener("storage", handleRoleChange);

    return () => {
      window.removeEventListener("roleChanged", handleRoleChange);
      window.removeEventListener("storage", handleRoleChange);
    };
  }, []);

  // Poll backend for role updates so if admin re-updates the role, access changes automatically
  useEffect(() => {
    const syncRoleWithBackend = async () => {
      try {
        const res = await apiFetch("/admin/profile", { method: "GET" });
        if (res.ok) {
          const data = await res.json();
          const profile = Array.isArray(data) ? data[0] : data;
          if (profile && profile.role) {
            const freshRole = normalizeRole(profile.role);
            const currentRole = getCurrentUserRole();
            if (freshRole !== currentRole) {
              localStorage.setItem("userRole", freshRole);
              setRole(freshRole);
              window.dispatchEvent(
                new CustomEvent("roleChanged", { detail: { role: freshRole } })
              );
            }
          }
        }
      } catch (err) {
        // ignore network error
      }
    };

    syncRoleWithBackend();
    const interval = setInterval(syncRoleWithBackend, 5000);
    window.addEventListener("focus", syncRoleWithBackend);

    return () => {
      clearInterval(interval);
      window.removeEventListener("focus", syncRoleWithBackend);
    };
  }, []);

  // Check if current route is permitted under active role; if not, redirect to dashboard home
  useEffect(() => {
    const path = location.pathname;
    if (path.endsWith("/devices") && !hasPermission("canAccessDevices", role)) {
      navigate("/dashboard/home", { replace: true });
    } else if (path.includes("/device-qc") && !hasPermission("canAccessDeviceQc", role)) {
      navigate("/dashboard/home", { replace: true });
    } else if (
      (path.includes("/firmwareupdate") || path.includes("/firmwares")) &&
      !hasPermission("canAccessFirmware", role)
    ) {
      navigate("/dashboard/home", { replace: true });
    } else if (path.includes("/users") && !hasPermission("canAccessUserManagement", role)) {
      navigate("/dashboard/home", { replace: true });
    } else if (
      (path.includes("/master-mac") ||
        path.includes("/mac-comparison") ||
        path.includes("/mac-reconciliation")) &&
      !hasPermission("canAccessMasterMac", role)
    ) {
      navigate("/dashboard/home", { replace: true });
    } else if (path.includes("/iqc-data") && !hasPermission("canAccessIqcData", role)) {
      navigate("/dashboard/home", { replace: true });
    } else if (path.includes("/app-users") && !hasPermission("canAccessAppUsers", role)) {
      navigate("/dashboard/home", { replace: true });
    } else if (path.includes("/customer-care") && !hasPermission("canAccessCustomerCare", role)) {
      navigate("/dashboard/home", { replace: true });
    }
  }, [role, location.pathname, navigate]);

  return (
    <div className={`DashBoard ${isNavHide ? "collapsed" : ""}`}>
      <nav className={`db_nb ${isNavHide ? "collapsed" : ""}`}>
        <aside className={`db_nb_body ${isNavHide ? "collapsed" : ""}`}>
          <h4 className={`logo-section ${isNavHide ? "collapsed" : ""}`}>
            <img src={Studds_Logo} alt="Studds Logo" />
          </h4>
          <ul>
            {menuItems.map(({ path, label, icon: Icon }) => {
              const ItemIcon = Icon || (path === "devices" ? MdVerifiedUser : null);
              return (
                <li key={path}>
                  <NavLink
                    to={path}
                    className={({ isActive }) =>
                      `nav-link ${isActive ? "active" : ""} ${isNavHide ? "collapsed" : ""}`
                    }
                  >
                    {ItemIcon ? <ItemIcon size="22" /> : null}
                    {!isNavHide && <span>{label}</span>}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </aside>
      </nav>

      <main className="db_nb_selected">
        <section className="db_content">
          <Outlet />
        </section>
      </main>
    </div>
  );
}

export default DashBoard;